import React, { useMemo, useState } from 'react';
import { FileCheck } from 'lucide-react';
import { getFacilityCollectionSnapshot, useAdminEmiPayments, useUnifiedCustomers } from '../../lib/workspaceStore';
import { PageSearchBar } from '../../components/common/PageSearchBar';
import { inRange } from '../../lib/dateFilter';
import { useDashboardDetailDates } from '../../lib/useDashboardDetailDates';
import type { ExportRow } from '../../lib/exportReports';
import { DashboardDetailShell, DashboardMetricCard, type DashboardDetailDateProps } from './DashboardDetailShell';
import { mediaSrc } from '../../lib/mediaUrl';

interface ClosedHPsProps extends DashboardDetailDateProps {
  onClose: () => void;
}

export function ClosedHPs({ onClose, initialDateRange }: ClosedHPsProps) {
  const customers = useUnifiedCustomers();
  const payments = useAdminEmiPayments();
  const dr = useDashboardDetailDates(initialDateRange);
  const [query, setQuery] = useState('');

  const rows = useMemo(() => {
    const out: {
      id: string;
      name: string;
      displayRef: string;
      avatar: string;
      totalAmount: number;
      tenure: string;
      vehicleDetails: string;
      facilityCount: number;
      closedInPeriod: boolean;
    }[] = [];
    for (const c of customers) {
      if (!c.facilities.length) continue;
      const allClosed = c.facilities.every((f) => getFacilityCollectionSnapshot(f).isLedgerClosed);
      if (!allClosed) continue;
      const facilityIds = new Set(c.facilities.map((f) => f.recordId));
      const closurePayments = payments.filter(
        (p) => facilityIds.has(p.recordId) && (p.facilityPayoff || p.entryType === 'closure')
      );
      const lastClosure = closurePayments.sort(
        (a, b) => new Date(b.paidAt).getTime() - new Date(a.paidAt).getTime()
      )[0];
      const closedInPeriod = lastClosure ? inRange(lastClosure.paidAt, dr.range) : inRange(c.createdAt, dr.range);
      if (!closedInPeriod) continue;
      const titles = c.facilities.map((f) => f.title).join(', ');
      const total = c.facilities.reduce((s, f) => s + f.principal, 0);
      out.push({
        id: c.id,
        name: c.name,
        displayRef: c.displayRef || c.phone || c.name,
        avatar: c.avatar,
        totalAmount: total,
        tenure: `${c.facilities.reduce((s, f) => s + f.periods, 0)} mo (Σ tenor)`,
        vehicleDetails: titles,
        facilityCount: c.facilities.length,
        closedInPeriod,
      });
    }
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }, [customers, payments, dr.range]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.displayRef.toLowerCase().includes(q) ||
        r.vehicleDetails.toLowerCase().includes(q)
    );
  }, [rows, query]);

  const totalPrincipal = useMemo(() => rows.reduce((s, r) => s + r.totalAmount, 0), [rows]);

  const exportRows: ExportRow[] = useMemo(
    () =>
      filtered.map((r) => ({
        Customer: r.name,
        Mobile: r.displayRef,
        Facilities: r.vehicleDetails,
        FacilityCount: r.facilityCount,
        PrincipalTotal: r.totalAmount,
        Tenure: r.tenure,
      })),
    [filtered]
  );

  return (
    <DashboardDetailShell
      onClose={onClose}
      title="Closed hire purchases"
      subtitle="Customers with all facilities closed on the EMI ledger in the selected period."
      icon={FileCheck}
      iconClassName="text-teal-700"
      exportTitle="Closed HP Report"
      exportFilename="closed-hire-purchases.xls"
      exportRows={exportRows}
      dateRange={dr.range}
      datePreset={dr.preset}
      onDateStart={dr.setStart}
      onDateEnd={dr.setEnd}
      onDatePreset={dr.setPreset}
      onDateClear={dr.clear}
    >
      <div className="grid grid-cols-2 gap-3 sm:max-w-lg">
        <DashboardMetricCard label="Closed customers" value={rows.length} accent />
        <DashboardMetricCard label="Principal Σ" value={`₹${totalPrincipal.toLocaleString('en-IN')}`} />
      </div>

      <PageSearchBar
        id="dash-closed-hps-search"
        value={query}
        onChange={setQuery}
        placeholder="Search customer, mobile, or facility…"
        className="max-w-md"
      />

      <div className="app-card overflow-hidden shadow-sm ring-1 ring-zinc-950/[0.03]">
        <div className="border-b border-zinc-200 bg-zinc-50/90 px-4 py-3">
          <h2 className="text-sm font-semibold text-zinc-900">Ledger payoff complete</h2>
          <p className="text-xs text-zinc-500">{filtered.length} customer{filtered.length === 1 ? '' : 's'}</p>
        </div>
        <div className="app-table-wrap border-0">
          <table className="app-table min-w-full">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Mobile / ID</th>
                <th>Facilities</th>
                <th>Tenure</th>
                <th className="text-right">Principal Σ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-10 text-center text-sm text-zinc-500">
                    {rows.length === 0
                      ? 'No closed accounts in this date range.'
                      : 'No rows match your search.'}
                  </td>
                </tr>
              ) : (
                filtered.map((hp) => (
                  <tr key={hp.id} className="transition-colors hover:bg-teal-50/40">
                    <td className="whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <img src={mediaSrc(hp.avatar)} alt="" className="h-9 w-9 rounded-full object-cover ring-1 ring-zinc-200" />
                        <p className="font-medium text-zinc-900">{hp.name}</p>
                      </div>
                    </td>
                    <td className="whitespace-nowrap font-mono text-xs text-zinc-600">{hp.displayRef}</td>
                    <td className="max-w-xs text-sm text-zinc-700">{hp.vehicleDetails}</td>
                    <td className="whitespace-nowrap text-sm text-zinc-600">{hp.tenure}</td>
                    <td className="whitespace-nowrap text-right font-semibold tabular-nums text-zinc-900">
                      ₹{hp.totalAmount.toLocaleString('en-IN')}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardDetailShell>
  );
}
